<script setup lang="ts">
/**
 * CompanionIdentitySettings — L1+L3 da Proposta Lume, Fase 5.
 *
 * Painel onde o aprendiz escolhe como chamar o Companion e qual
 * visual usar. NÃO toca o LLM (Princípio XI). Acessado via
 * SettingsState.
 *
 * Constitucional:
 * - Princípio XI (Companion ≠ LLM): este painel é puramente UX.
 *   Nenhuma chave, prompt, ou rastro de LLM.
 * - P-UX-01: tudo opt-in; pode-se pular e usar "Companion" genérico.
 * - Princípio X (Dignidade): zero mascote manipulativo; apego
 *   saudável é OK, dependência emocional não.
 */
import { ref, computed } from "vue";
import { useCompanionIdentityStore } from "../store";
import { NAME_SUGGESTIONS, type CompanionVisualId } from "../types";
import CompanionVisual from "./CompanionVisual.vue";

interface Emits {
  (e: "close"): void;
}

const emit = defineEmits<Emits>();
const store = useCompanionIdentityStore();

const nameDraft = ref(store.identity.name);

const visuals: { value: CompanionVisualId; label: string }[] = [
  { value: "minimal-1", label: "Círculo" },
  { value: "minimal-2", label: "Quadrado arredondado" },
  { value: "minimal-3", label: "Losango" },
  { value: "none", label: "Sem visual" },
];

const customName = ref(!NAME_SUGGESTIONS.includes(nameDraft.value) && nameDraft.value !== "");

function onSaveName() {
  store.setName(nameDraft.value);
}

function onPickSuggestion(name: string) {
  if (name === "Outro") {
    customName.value = true;
    return;
  }
  nameDraft.value = name;
  customName.value = false;
  store.setName(name);
}

const currentName = computed(() => store.displayName());
</script>

<template>
  <section
    class="hlos-companion-id"
    role="region"
    aria-labelledby="hlos-companion-id-title"
  >
    <header class="hlos-companion-id__header">
      <h2 id="hlos-companion-id-title">Seu Companion</h2>
      <p class="hlos-companion-id__intro">
        Você pode dar um nome e escolher um visual. Essas escolhas são
        suas — não mudam como o Companion funciona, só como ele aparece
        para você. Se preferir, pode usar "Companion" sem nome.
      </p>
    </header>

    <fieldset class="hlos-companion-id__group">
      <legend>Nome</legend>
      <div class="hlos-companion-id__suggestions">
        <button
          v-for="name in NAME_SUGGESTIONS"
          :key="name"
          type="button"
          class="hlos-companion-id__chip"
          :class="{
            'hlos-companion-id__chip--active':
              !customName && nameDraft === name,
          }"
          :aria-pressed="!customName && nameDraft === name"
          :data-testid="`companion-name-${name}`"
          @click="onPickSuggestion(name)"
        >
          {{ name }}
        </button>
      </div>
      <div v-if="customName" class="hlos-companion-id__custom">
        <label for="companion-name-input" class="sr-only">
          Nome personalizado
        </label>
        <input
          id="companion-name-input"
          v-model="nameDraft"
          type="text"
          maxlength="32"
          placeholder="Seu nome para o Companion"
          data-testid="companion-name-input"
          @blur="onSaveName"
        />
      </div>
      <p v-if="currentName" class="hlos-companion-id__current" aria-live="polite">
        Atual: <strong>{{ currentName }}</strong>
      </p>
    </fieldset>

    <fieldset class="hlos-companion-id__group">
      <legend>Visual</legend>
      <div class="hlos-companion-id__visuals" role="radiogroup">
        <button
          v-for="v in visuals"
          :key="v.value"
          type="button"
          class="hlos-companion-id__visual"
          :class="{
            'hlos-companion-id__visual--active':
              store.identity.visual === v.value,
          }"
          :aria-pressed="store.identity.visual === v.value"
          :aria-label="`Visual: ${v.label}`"
          :data-testid="`companion-visual-${v.value}`"
          @click="store.setVisual(v.value)"
        >
          <CompanionVisual
            v-if="v.value !== 'none'"
            :visual="v.value"
            :size="48"
            :aria-label="`${v.label} selecionado`"
          />
          <span v-else class="hlos-companion-id__visual-none">—</span>
          <span class="hlos-companion-id__visual-label">{{ v.label }}</span>
        </button>
      </div>
    </fieldset>

    <footer class="hlos-companion-id__actions">
      <button
        type="button"
        class="hlos-companion-id__btn"
        data-testid="companion-id-close"
        @click="emit('close')"
      >
        Voltar
      </button>
    </footer>
  </section>
</template>

<style scoped>
.hlos-companion-id {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.hlos-companion-id__header h2 {
  margin: 0 0 0.25rem;
  font-size: 1.1rem;
}
.hlos-companion-id__intro {
  margin: 0;
  color: var(--hlos-color-neutral-600);
  font-size: 0.9em;
}
.hlos-companion-id__group {
  border: 1px solid var(--hlos-color-neutral-200);
  border-radius: 8px;
  padding: 0.75rem;
  margin: 0;
}
.hlos-companion-id__group legend {
  font-weight: 600;
  padding: 0 0.5rem;
}
.hlos-companion-id__suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0;
}
.hlos-companion-id__chip {
  font: inherit;
  padding: 0.4rem 0.9rem;
  border: 1px solid var(--hlos-color-neutral-300);
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
}
.hlos-companion-id__chip--active {
  background: var(--hlos-color-primary);
  color: var(--hlos-color-bg);
  border-color: var(--hlos-color-primary);
}
.hlos-companion-id__custom {
  margin-top: 0.5rem;
}
.hlos-companion-id__custom input {
  font: inherit;
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--hlos-color-neutral-300);
  border-radius: 6px;
  background: var(--hlos-color-bg);
  color: inherit;
}
.hlos-companion-id__current {
  margin: 0.5rem 0 0;
  font-size: 0.85em;
  color: var(--hlos-color-neutral-600);
}
.hlos-companion-id__visuals {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
.hlos-companion-id__visual {
  font: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem;
  border: 2px solid var(--hlos-color-neutral-200);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  min-width: 80px;
}
.hlos-companion-id__visual--active {
  border-color: var(--hlos-color-primary);
  background: var(--hlos-color-hover);
}
.hlos-companion-id__visual-none {
  display: inline-block;
  width: 48px;
  height: 48px;
  text-align: center;
  line-height: 48px;
  font-size: 1.5em;
  color: var(--hlos-color-neutral-500);
}
.hlos-companion-id__visual-label {
  font-size: 0.8em;
  color: var(--hlos-color-neutral-600);
}
.hlos-companion-id__actions {
  display: flex;
  justify-content: flex-end;
}
.hlos-companion-id__btn {
  font: inherit;
  padding: 0.5rem 1rem;
  border: 1px solid currentColor;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
